import React, { useState, useRef } from "react";
import { Box, Button } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import AutoComplete from "react-select";
import TransactionExportModal from "./FeeExportModal";
import SweetAlert2 from "react-sweetalert2";
import ProviderListModal from "../../shared/ProviderListModal";

interface ProviderAccount {
  label: string;
  value: string;
}

const InputStyle =
  "daterange-picker font-medium text-navy-700 h-10 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)", // Default border color
      borderRadius: "2px",
      _hover: {
        background: "gray", // Background color on hover
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray", // Background color for dates in the selected range
      color: "white", // Text color for dates in the selected range
    },
    selectedBtnProps: {
      background: "black", // Background color for selected date
      color: "white", // Text color for selected date
    },
    todayBtnProps: {
      // color: "#422afb", // Background color for today's date
      // fontWeight: "bold",
    },
  },
  inputProps: {
    // minWidth: "230px",
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)", // Apply custom box shadow
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9", // Border color for the calendar wrapper
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

const FeeSearch: React.FC<{
  showFilters: boolean;
  tableData: any;
  userPermissions: any;
  isLoading: boolean;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  selectedDates: any;
  setSelectedDates: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
}> = ({
  showFilters,
  tableData,
  userPermissions,
  isLoading,
  isFilterLoading,
  filters,
  setFilters,
  selectedDates,
  setSelectedDates,
  handleSearchSubmit,
  handleSearchClear,
}) => {
  const [isOpenExportModal, setIsOpenExportModal] = useState<boolean>(false);
  const [swalProps, setSwalProps] = useState<any>({});

  // Modal state for provider selection
  const [isProviderModalOpen, setIsProviderModalOpen] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);

  const providerAccountAutoCompleteRef = useRef(null);
  const [
    providerAccountAutoCompleteOptions,
    setProviderAccountAutoCompleteOptions,
  ] = useState<any>(null);

  const handleClear = () => {
    setSelectedProvider(null);
    setProviderAccountAutoCompleteOptions(null);
    providerAccountAutoCompleteRef.current?.clearValue();
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    handleSearchClear();
  };

  // Handler for provider selection from modal
  const handleProviderSelect = (providerData: any) => {
    setSelectedProvider(providerData);
    localStorage.setItem("providerIdToFilterTransaction", providerData.id);
    localStorage.setItem("providerNameToFilterTransaction", providerData.name);
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    setProviderAccountAutoCompleteOptions(null);
    providerAccountAutoCompleteRef.current?.clearValue();
    buildUpProviderAccount(providerData.id, providerData);
    setFilters({ ...filters, provider_account_id: "" });
  };

  // Clear provider selection
  const handleClearProvider = () => {
    setSelectedProvider(null);
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    setProviderAccountAutoCompleteOptions(null);
    providerAccountAutoCompleteRef.current?.clearValue();
    setFilters({ ...filters, provider_account_id: "" });
  };

  const handleProviderAccountAutoCompleteChange = (item: any) => {
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    if (item !== null) {
      localStorage.setItem("providerAccountIdToFilterTransaction", item.value);
      localStorage.setItem("providerAccountNameToFilterTransaction", item.label);
      setFilters({ ...filters, provider_account_id: item.value });
    } else {
      setFilters({ ...filters, provider_account_id: "" });
      setProviderAccountAutoCompleteOptions(null);
      if (selectedProvider) {
        buildUpProviderAccount(selectedProvider.id, selectedProvider);
      }
    }
  };

  const noProviderAccountOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return (
      <div className="text-sm">
        Provider account not found for "{inputValue}"
      </div>
    );
  };

  const buildUpProviderAccount = (
    providerIdToFilterProviderAccount: string,
    providerData?: any
  ) => {
    const providerAccountList: ProviderAccount[] = [];
    if (providerData && providerIdToFilterProviderAccount !== "") {
      if (
        providerData.provider_account &&
        Array.isArray(providerData.provider_account) &&
        providerData.provider_account.length > 0
      ) {
        providerData.provider_account.forEach((account: any) => {
          if (account?.id && account?.account_name) {
            providerAccountList.push({
              label: `${providerData.name} (${account.account_name})`,
              value: account.id,
            });
          }
        });
      }
      setProviderAccountAutoCompleteOptions(providerAccountList);
    }
  };

  const closeExportModal = () => {
    setIsOpenExportModal(false);
  };

  // If not showing filters, render nothing (button lives outside in the toolbar)
  if (!showFilters) return null;

  return (
    <>
      <TransactionExportModal
        setSwalProps={setSwalProps}
        isOpen={isOpenExportModal}
        onClose={() => closeExportModal()}
        userPermissions={userPermissions}
      />

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>

      <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="search-picker-custom tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Date
            </label>
            <RangeDatepicker
              disabled={isFilterLoading || isLoading}
              selectedDates={selectedDates}
              onDateChange={setSelectedDates}
              propsConfigs={dateRangePropsConfigs}
              configs={{
                dateFormat: "MMM d, yyyy",
              }}
              maxDate={new Date()}
            />
          </div>
          <div className="tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Provider
            </label>
            <Tooltip
              title={selectedProvider ? selectedProvider.name : "Please Select"}
              arrow
              placement="top"
            >
              <div
                onClick={() => !isFilterLoading && !isLoading && setIsProviderModalOpen(true)}
                className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                  isFilterLoading || isLoading
                    ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                    : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                }`}
              >
                <span className="truncate">
                  {selectedProvider ? selectedProvider.name : "Please Select"}
                </span>
                {selectedProvider && !isFilterLoading && !isLoading && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleClearProvider(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
            </Tooltip>
          </div>
          <div className="tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Provider Account
            </label>
            <AutoComplete
              ref={providerAccountAutoCompleteRef}
              className="autocomplete-override"
              isDisabled={
                isFilterLoading ||
                isLoading ||
                !selectedProvider ||
                providerAccountAutoCompleteOptions === null ||
                providerAccountAutoCompleteOptions.length === 0
              }
              placeholder={
                !selectedProvider
                  ? "Please Select Provider"
                  : providerAccountAutoCompleteOptions === null
                  ? "N/A"
                  : providerAccountAutoCompleteOptions.length === 0
                  ? "N/A"
                  : "Please Select"
              }
              options={providerAccountAutoCompleteOptions}
              classNamePrefix="custom-autocomplete"
              onChange={(item: any) =>
                handleProviderAccountAutoCompleteChange(item)
              }
              noOptionsMessage={noProviderAccountOptionsMessageRenderer}
            />
          </div>
        </div>
        <div className="mt-4 flex flex-1 items-end justify-end gap-2">
          <Button
            isDisabled={!tableData || isLoading}
            onClick={handleClear}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            Clear
          </Button>
          <Button
            isLoading={isLoading}
            loadingText={"Generating..."}
            isDisabled={filters.provider_account_id === "" || isLoading}
            onClick={handleSearchSubmit}
            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="relative z-10 tracking-tight">See Report</span>
            <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
          </Button>
        </div>
      </Box>

      {/* Provider Selection Modal */}
      {isProviderModalOpen && (
        <ProviderListModal
          isOpen={isProviderModalOpen}
          onClose={() => setIsProviderModalOpen(false)}
          onSelect={handleProviderSelect}
        />
      )}
    </>
  );
};

export default FeeSearch;
